import { useCallback, useRef, useState } from 'react'
import { ArrowSquareOut, FilePdf, UploadSimple, WarningCircle } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { IconPad } from '@/components/ui/icon-pad'
import type { ScheduleFile } from '../pages/mini-apps/teacher-dashboard'

const MAX_BYTES = 20 * 1024 * 1024

type UploadState = 'idle' | 'dragging' | 'uploading' | 'error'

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`
}

function shortName(name: string): string {
  return name.length > 42 ? `${name.slice(0, 39)}…` : name
}

function validate(file: File): string | null {
  if (file.type !== 'application/pdf')
    return `« ${shortName(file.name)} » n'est pas un PDF. Seuls les fichiers PDF sont acceptés.`
  if (file.size > MAX_BYTES)
    return `« ${shortName(file.name)} » pèse ${formatSize(file.size)} : la limite est de 20 Mo.`
  return null
}

const ACTION_BASE =
  'inline-flex items-center justify-center gap-2 min-h-11 px-4 rounded-full font-sans text-[13.5px] font-bold cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-naia-blue'

function ErrorNote({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="flex items-start gap-1.5 m-0 px-4 pb-4 text-[12.5px] leading-snug text-destructive"
    >
      <WarningCircle size={15} weight="fill" className="mt-px flex-shrink-0" />
      {message}
    </p>
  )
}

export function ScheduleDropZone({ scheduleFile, onUpload, onOpen }: {
  scheduleFile: ScheduleFile | null
  onUpload: (file: File) => Promise<void>
  onOpen: () => void
}) {
  const [state, setState] = useState<UploadState>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragCount = useRef(0)

  const processFile = useCallback(async (file: File) => {
    const err = validate(file)
    if (err) {
      setErrorMsg(err)
      setState('error')
      return
    }
    setErrorMsg(null)
    setState('uploading')
    try {
      await onUpload(file)
      setState('idle')
    } catch {
      setErrorMsg(`L'envoi de « ${shortName(file.name)} » a échoué. Vérifiez votre connexion, puis réessayez.`)
      setState('error')
    }
  }, [onUpload])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCount.current += 1
    setErrorMsg(null)
    setState(prev => prev === 'uploading' ? prev : 'dragging')
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCount.current -= 1
    if (dragCount.current <= 0) {
      dragCount.current = 0
      setState(prev => prev === 'dragging' ? 'idle' : prev)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCount.current = 0
    const file = e.dataTransfer.files[0]
    if (file) processFile(file)
    else setState(prev => prev === 'dragging' ? 'idle' : prev)
  }, [processFile])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
    e.target.value = ''
  }, [processFile])

  const openPicker = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const isDragging = state === 'dragging'
  const isUploading = state === 'uploading'
  const isError = state === 'error'
  const showsFile = Boolean(scheduleFile) && !isDragging && !isUploading

  return (
    <div
      className={cn(
        'rounded-[10px] border-2 transition-colors duration-150',
        showsFile ? 'border-solid bg-muted' : 'border-dashed',
        isDragging
          ? 'border-naia-blue bg-naia-info-bg'
          : isError
          ? 'border-destructive'
          : 'border-border',
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="sr-only"
        aria-label="Sélectionner un PDF d'emploi du temps"
        onChange={handleInputChange}
        tabIndex={-1}
      />

      {isUploading ? (
        <div
          role="status"
          aria-live="polite"
          className="flex flex-col items-center justify-center py-8 gap-2"
        >
          <div className="w-5 h-5 border-2 border-naia-blue border-t-transparent rounded-full animate-spin" />
          <span className="text-[13.5px] font-medium text-muted-foreground">Envoi en cours…</span>
        </div>
      ) : isDragging ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2 pointer-events-none select-none">
          <UploadSimple size={28} weight="fill" className="text-naia-blue" />
          <span className="text-[14px] font-semibold text-naia-blue">
            {scheduleFile ? 'Déposez pour remplacer' : 'Déposez le fichier ici'}
          </span>
        </div>
      ) : scheduleFile ? (
        <div className="flex flex-wrap items-center gap-3 p-3">
          <IconPad variant="blue" size="lg">
            <FilePdf size={24} weight="fill" />
          </IconPad>
          <div className="flex-1 min-w-[7rem] flex flex-col gap-0.5">
            <span className="text-[14px] font-bold text-foreground leading-snug break-all line-clamp-2">
              {scheduleFile.name}
            </span>
            <span className="text-[12.5px] text-muted-foreground tabular-nums">
              PDF · {formatSize(scheduleFile.fileSize)}
            </span>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={onOpen}
              disabled={!scheduleFile.signedUrl}
              className={cn(
                ACTION_BASE,
                'flex-1 sm:flex-none border-none bg-primary text-primary-foreground hover:bg-naia-blue-deep disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <ArrowSquareOut size={16} weight="bold" />
              Ouvrir
            </button>
            <button
              type="button"
              onClick={openPicker}
              className={cn(
                ACTION_BASE,
                'flex-1 sm:flex-none border border-border bg-card text-foreground hover:bg-naia-info-bg hover:text-naia-blue-deep',
              )}
            >
              <UploadSimple size={16} weight="bold" />
              Remplacer
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="w-full flex flex-col items-center justify-center gap-2 py-8 px-4 text-center rounded-[8px] bg-transparent border-none cursor-pointer font-sans focus-visible:outline-2 focus-visible:outline-naia-blue"
        >
          <UploadSimple size={28} className="text-muted-foreground" />
          <span className="text-[14px] font-semibold text-foreground">
            Déposez votre emploi du temps
          </span>
          <span className="text-[12.5px] text-muted-foreground">
            ou cliquez pour choisir un fichier · PDF, 20 Mo maximum
          </span>
        </button>
      )}

      {isError && errorMsg && <ErrorNote message={errorMsg} />}
    </div>
  )
}
